"use client";
import { Card, CardContent, CardHeader } from "./card-pricing";
import { Sparkles as SparklesComp } from "./sparkles-pricing";
import { TimelineContent } from "./timeline-content-pricing";
import { VerticalCutReveal } from "./vertical-cut-reveal-pricing";
import NumberFlow from "@number-flow/react";
import { ReactNode, useRef } from "react";
import { useTranslation } from "react-i18next";
import "./pricing-section.css";
import FighterJetIcon from "../../assets/icons/FighterJetIcon";
import RocketIcon from "../../assets/icons/RocketIcon";
import BuildingIcon from "../../assets/icons/BuildingIcon";
import CheckIcon from "../../assets/icons/CheckIcon";

interface PricingSectionAzurePrivateCloudProps {
  openContactModal?: () => void;
}

interface PlanConfig {
  name: string;
  icon: ReactNode;
  price: number | string;
  priceSuffix?: string;
  priceNote?: string;
  description?: string;
  buttonText: string;
  buttonVariant: "default" | "outline";
  popular?: boolean;
  includes: string[];
  footerNote?: string;
}

export default function PricingSectionAzurePrivateCloud({ openContactModal }: PricingSectionAzurePrivateCloudProps) {
  const { t } = useTranslation();
  const pricingRef = useRef<HTMLDivElement>(null);

  const plans: PlanConfig[] = [
    {
      name: t('privateCloud.pricing.cards.monthly.title', "Stockage mensuel"),
      icon: <FighterJetIcon />,
      price: 15,
      priceSuffix: t('privateCloud.pricing.priceUnit.month', '/ mois'),
      priceNote: t('privateCloud.pricing.cards.monthly.priceNote', 'pour un utilisateur'),
      buttonText: t('privateCloud.pricing.contactButton', "Nous contacter"),
      buttonVariant: "outline" as const,
      footerNote: t('privateCloud.pricing.taxNote', 'Prix HT'),
      includes: [
        t('privateCloud.pricing.cards.monthly.features.0', "1 To d'espace disque chiffré"),
        t('privateCloud.pricing.cards.monthly.features.1', "10 € par utilisateur supplémentaire"),
        t('privateCloud.pricing.cards.monthly.features.2', "Synchronisation de vos données sur tous vos périphériques"),
        t('privateCloud.pricing.cards.monthly.features.3', "Un ingénieur dédié et accessible"),
        t('privateCloud.pricing.cards.monthly.features.4', "Sans engagement"),
      ],
    },
    {
      name: t('privateCloud.pricing.cards.annual.title', "Stockage annuel"),
      icon: <RocketIcon />,
      price: 150,
      priceSuffix: t('privateCloud.pricing.priceUnit.year', '/ an'),
      priceNote: t('privateCloud.pricing.cards.annual.priceNote', 'pour un utilisateur'),
      buttonText: t('privateCloud.pricing.contactButton', "Nous contacter"),
      buttonVariant: "default" as const,
      popular: true,
      includes: [
        t('privateCloud.pricing.cards.annual.features.0', "1 To d'espace disque chiffré"),
        t('privateCloud.pricing.cards.annual.features.1', "10 € par utilisateur supplémentaire"),
        t('privateCloud.pricing.cards.annual.features.2', "Synchronisation de vos données sur tous vos périphériques"),
        t('privateCloud.pricing.cards.annual.features.3', "Un ingénieur dédié et accessible"),
        t('privateCloud.pricing.cards.annual.features.4', "Sans engagement"),
      ],
      footerNote: t('privateCloud.pricing.taxNote', 'Prix HT'),
    },
    {
      name: t('privateCloud.pricing.cards.hybrid.title', "Installation sur site"),
      icon: <BuildingIcon />,
      price: 2000,
      priceNote: t('privateCloud.pricing.cards.hybrid.priceNote', '+ prix du matériel'),
      buttonText: t('privateCloud.pricing.contactButton', "Nous contacter"),
      buttonVariant: "outline" as const,
      includes: [
        t('privateCloud.pricing.cards.hybrid.features.0', "Espace disque de votre choix"),
        t('privateCloud.pricing.cards.hybrid.features.1', "15 ou 20 € par utilisateur supplémentaire selon forfait"),
        t('privateCloud.pricing.cards.hybrid.features.2', "Hébergé chez vous"),
        t('privateCloud.pricing.cards.hybrid.features.3', "Assistance en cas de panne"),
        t('privateCloud.pricing.cards.hybrid.features.4', "Sans engagement"),
      ],
      footerNote: t('privateCloud.pricing.taxNote', 'Prix HT'),
    },
  ];

  const revealVariants = {
    visible: (i: number) => ({
      y: 0,
      opacity: 1,
      filter: "blur(0px)",
      transition: {
        delay: i * 0.1,
        duration: 0.2,
      },
    }),
    hidden: {
      filter: "blur(10px)",
      y: -20,
      opacity: 0,
    },
  };

  return (
    <div
      className="tw-mx-auto tw-relative tw-bg-black tw-overflow-hidden"
      ref={pricingRef}
    >
      <TimelineContent
        animationNum={0}
        timelineRef={pricingRef}
        customVariants={revealVariants}
        once={true}
        className="tw-absolute tw-top-0 tw-h-96 tw-w-screen tw-overflow-hidden [mask-image:radial-gradient(50%_50%,white,transparent)]"
      >
        <div className="tw-absolute tw-bottom-0 tw-left-0 tw-right-0 tw-top-0 tw-bg-[linear-gradient(to_right,#ffffff2c_1px,transparent_1px),linear-gradient(to_bottom,#3a3a3a01_1px,transparent_1px)] tw-bg-[size:70px_80px]"></div>
        <SparklesComp
          density={1800}
          speed={1}
          color="#FFFFFF"
          className="tw-absolute tw-inset-x-0 tw-bottom-0 tw-h-full tw-w-full [mask-image:radial-gradient(50%_50%,white,transparent_85%)]"
        />
      </TimelineContent>
      <TimelineContent
        animationNum={0}
        timelineRef={pricingRef}
        customVariants={revealVariants}
        once={true}
        className="tw-absolute tw-left-0 tw-top-[-114px] tw-w-full tw-h-[100vh] tw-flex tw-flex-col tw-items-start tw-justify-start tw-content-start tw-flex-none tw-flex-nowrap tw-gap-2.5 tw-p-0 tw-z-0 tw-overflow-hidden tw-pointer-events-none"
      >
        <div className="framer-1i5axl2">
          <div
            className="tw-absolute tw-left-[-568px] tw-right-[-568px] tw-top-0 tw-h-[2053px] tw-flex-none tw-rounded-full"
            style={{
              border: "200px solid var(--gradient-color)",
              filter: "blur(92px)",
              WebkitFilter: "blur(92px)",
            }}
            data-border="true"
            data-framer-name="Ellipse 1"
          ></div>
          <div
            className="tw-absolute tw-left-[-568px] tw-right-[-568px] tw-top-0 tw-h-[2053px] tw-flex-none tw-rounded-full"
            style={{
              border: "200px solid var(--gradient-color)",
              filter: "blur(92px)",
              WebkitFilter: "blur(92px)",
            }}
            data-border="true"
            data-framer-name="Ellipse 2"
          ></div>
        </div>
      </TimelineContent>

      <article className="tw-text-center tw-mb-6 tw-pt-32 tw-max-w-3xl tw-mx-8 md:tw-mx-auto tw-space-y-2 tw-relative tw-z-50">
        <h2 className="tw-text-4xl tw-font-medium tw-text-white">
          <VerticalCutReveal
            splitBy="words"
            staggerDuration={0.05}
            staggerFrom="first"
            reverse={true}
            containerClassName="tw-justify-center tw-pb-[10px]"
            transition={{
              type: "spring",
              stiffness: 400,
              damping: 30,
              delay: 0,
            }}
          >
            Votre cloud privé sécurisé sur NAS
          </VerticalCutReveal>
        </h2>

        <TimelineContent
          as="div"
          animationNum={1}
          timelineRef={pricingRef}
          customVariants={revealVariants}
          once={true}
          className="tw-text-gray-300 tw-text-center tw-pb-[40px] tw-flex tw-justify-center"
        >
          <ul className="tw-max-w-fit">
            <li className="tw-flex tw-items-center tw-gap-2">
              <span className="tw-h-2.5 tw-w-2.5 tw-bg-blue-500 tw-rounded-full"></span>
              <span>Maîtrise totale de vos données</span>
            </li>
            <li className="tw-flex tw-items-center tw-gap-2">
              <span className="tw-h-2.5 tw-w-2.5 tw-bg-blue-500 tw-rounded-full"></span>
              <span>Sécurité et chiffrement avancés</span>
            </li>
            <li className="tw-flex tw-items-center tw-gap-2">
              <span className="tw-h-2.5 tw-w-2.5 tw-bg-blue-500 tw-rounded-full"></span>
              <span>Collaboration fluide et sécurisée</span>
            </li>
            <li className="tw-flex tw-items-center tw-gap-2">
              <span className="tw-h-2.5 tw-w-2.5 tw-bg-blue-500 tw-rounded-full"></span>
              <span>Conformité RGPD garantie</span>
            </li>
          </ul>
        </TimelineContent>

      </article>

      <div
        className="tw-absolute tw-top-0 tw-left-[10%] tw-right-[10%] tw-w-[80%] tw-h-full tw-z-0"
        style={{
          backgroundImage: `
        radial-gradient(circle at center, var(--gradient-color) 0%, transparent 70%)
      `,
          opacity: 0.6,
          mixBlendMode: "multiply",
        }}
      />

      {/* Centered blue gradient halo behind cards */}
      <div
        className="tw-absolute tw-top-[60%] tw-left-1/2 tw-transform tw--translate-x-1/2 tw--translate-y-1/2 tw-w-[1400px] tw-h-[800px] tw-z-10 tw-pointer-events-none"
        style={{
          background: `radial-gradient(ellipse at center, rgba(49, 49, 245, 0.6) 0%, rgba(49, 49, 245, 0.3) 40%, rgba(49, 49, 245, 0.1) 70%, transparent 100%)`,
          filter: "blur(70px)",
          borderRadius: "50%",
        }}
      />

      <div className="tw-grid md:tw-grid-cols-3 tw-max-w-5xl tw-gap-4 tw-pb-6 tw-mx-8 md:tw-mx-auto tw-relative tw-z-20">
        {plans.map((plan, index) => (
          <TimelineContent
            key={plan.name}
            as="div"
            animationNum={3 + index}
            timelineRef={pricingRef}
            customVariants={revealVariants}
            once={true}
            >
              <Card
                className={`tw-relative tw-text-white ${
                  plan.popular
                    ? "tw-border-neutral-800 tw-bg-gradient-to-r tw-from-neutral-900 tw-via-neutral-800 tw-to-neutral-900 tw-shadow-[0px_-13px_300px_0px_#0900ff] tw-z-20"
                  : index === 0
                    ? "tw-border-neutral-800/80 tw-bg-gradient-to-r tw-from-neutral-900/45 tw-via-neutral-800/45 tw-to-neutral-900/45 tw-backdrop-blur-md tw-z-20"
                    : "tw-border-neutral-800 tw-bg-gradient-to-r tw-from-neutral-900 tw-via-neutral-800 tw-to-neutral-900 tw-z-10"
              }`}
            >
              <CardHeader className="tw-text-left">
                <div className="tw-flex tw-items-center tw-gap-4 tw-mb-4">
                  <div className="tw-flex tw-h-12 tw-w-12 tw-items-center tw-justify-center tw-rounded-full tw-bg-blue-500/10 tw-text-blue-400 tw-scale-75 tw-origin-center">
                    {plan.icon}
                  </div>
                  <h3 className="tw-text-3xl tw-font-semibold">{plan.name}</h3>
                </div>
                <div className="tw-flex tw-items-baseline tw-gap-2">
                  {typeof plan.price === "number" ? (
                    <>
                      <span className="tw-text-4xl tw-font-semibold">
                        <NumberFlow
                          format={{
                            currency: "EUR",
                          }}
                          value={plan.price}
                          className="tw-text-4xl tw-font-semibold"
                        />
                      </span>
                      <span className="tw-text-2xl tw-font-semibold">€</span>
                    </>
                  ) : (
                    <span className="tw-text-4xl tw-font-semibold tw-text-blue-400">
                      {plan.price}
                    </span>
                  )}
                  {plan.priceSuffix && (
                    <span className="tw-text-base tw-font-[550] tw-text-gray-300">{plan.priceSuffix}</span>
                  )}
                </div>
                {plan.priceNote && (
                  <div className="tw-text-sm tw-text-gray-400 tw-font-[550] tw-mt-1">
                    {plan.priceNote}
                  </div>
                )}
                {plan.description && (
                  <p className="tw-text-sm tw-text-gray-300 tw-mt-4 tw-font-[550]">{plan.description}</p>
                )}
              </CardHeader>

              <CardContent className="tw-pt-0">
                <button
                  onClick={() => {
                    openContactModal?.();
                  }}
                  className={`tw-w-full tw-mb-6 tw-p-4 tw-text-xl tw-rounded-xl ${
                    plan.popular
                      ? "tw-bg-gradient-to-t tw-from-blue-500 tw-to-blue-600 tw-shadow-lg tw-shadow-blue-800 tw-border tw-border-blue-500 tw-text-white"
                      : index === 0
                        ? "tw-bg-gradient-to-t tw-from-neutral-950/50 tw-to-neutral-600/50 tw-backdrop-blur-sm tw-shadow-lg tw-shadow-neutral-900/30 tw-border tw-border-neutral-800/60 tw-text-white"
                        : plan.buttonVariant === "outline"
                          ? "tw-bg-gradient-to-t tw-from-neutral-950 tw-to-neutral-600 tw-shadow-lg tw-shadow-neutral-900 tw-border tw-border-neutral-800 tw-text-white"
                          : ""
                  }`}
                >
                  {plan.buttonText}
                </button>

                <div className="tw-space-y-3 tw-pt-4 tw-border-t tw-border-neutral-700">
                  <ul className="tw-space-y-3">
                    {plan.includes.map((feature: string, featureIndex: number) => (
                      <li
                        key={featureIndex}
                        className="tw-flex tw-items-start tw-gap-3"
                      >
                        <span className="tw-mt-0.5 tw-text-blue-400 tw-flex-shrink-0">
                          <CheckIcon />
                        </span>
                        <span className="tw-text-sm tw-text-gray-300 tw-font-[550] tw-text-left">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  {plan.footerNote && (
                    <div className="tw-text-xs tw-text-gray-500 tw-text-right tw-font-[550]">
                      {plan.footerNote}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TimelineContent>
        ))}
      </div>
    </div>
  );
}
